import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { Spin } from "antd";
import { Home } from "@/routes/Home";
import { NotFound } from "@/routes/NotFound";
import { Login } from "@/routes/Login";
import { Signup } from "@/routes/Signup";
import { EmailVerify } from "@/routes/EmailVerify";
import { ForgetPassword } from "@/routes/ForgetPassword";
import { ResetPassword } from "@/routes/ResetPassword";
import { InviteAccept } from "@/routes/InviteAccept";
import { CourseList } from "@/routes/CourseList";
import { CourseDetail } from "@/routes/CourseDetail";
import { CourseSrsReview } from "@/routes/CourseSrsReview";
import { LessonDetail } from "@/routes/LessonDetail";
import { MyCourses } from "@/routes/MyCourses";
import { Profile } from "@/routes/Profile";
import { MajorPublicDetail } from "@/routes/MajorPublicDetail";
import { StudyPlan } from "@/routes/StudyPlan";
import { MyOrders } from "@/routes/MyOrders";
import { MyProgress } from "@/routes/MyProgress";
import { PaymentReturn } from "@/routes/PaymentReturn";
import { QuizList } from "@/routes/QuizList";
import { QuizConsent } from "@/routes/QuizConsent";
import { QuizTake } from "@/routes/QuizTake";
import { QuizReview } from "@/routes/QuizReview";
import { UserGuide } from "@/routes/UserGuide";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/AppLayout";
import { AdminRouteGuard } from "@/admin/AdminRouteGuard";
import { AdminLayout } from "@/admin/AdminLayout";
// Sprint 24 B4 — Career Connect public routes (lazy load — pitch ĐH module)
const CareerLanding = lazy(() =>
  import("@/features/careers/CareerLanding").then((m) => ({ default: m.CareerLanding })),
);
const CareerPathDetail = lazy(() =>
  import("@/features/careers/CareerPathDetail").then((m) => ({
    default: m.CareerPathDetail,
  })),
);
const CareerPartnerDetail = lazy(() =>
  import("@/features/careers/CareerPartnerDetail").then((m) => ({
    default: m.CareerPartnerDetail,
  })),
);
const CareersPrivacy = lazy(() =>
  import("@/features/careers/PrivacyNotice").then((m) => ({ default: m.PrivacyNotice })),
);
const CareerRegister = lazy(() =>
  import("@/features/careers/CareerRegister").then((m) => ({ default: m.CareerRegister })),
);
const AdminCareerInterests = lazy(() =>
  import("@/features/careers/AdminInterestList").then((m) => ({
    default: m.AdminInterestList,
  })),
);
// Sprint 25 B5 — AI Lesson Designer (instructor PDF → outline), route dưới /admin/designer
const DesignerHome = lazy(() =>
  import("@/features/authoring/DesignerHome").then((m) => ({ default: m.DesignerHome })),
);
const DesignerWizard = lazy(() =>
  import("@/features/authoring/DesignerWizard").then((m) => ({
    default: m.DesignerWizard,
  })),
);
// Sprint 10 B5 — route-level lazy load admin pages (giảm bundle main < 500 KB).
// AdminProctorReview cũng lazy vì chỉ dùng cho proctor team.
// Default export wrapper cho mỗi component vì React.lazy yêu cầu default export.
const AdminProctorReview = lazy(() =>
  import("@/routes/AdminProctorReview").then((m) => ({ default: m.AdminProctorReview })),
);
const AdminDashboard = lazy(() =>
  import("@/admin/routes/Dashboard").then((m) => ({ default: m.AdminDashboard })),
);
const AdminForbidden = lazy(() =>
  import("@/admin/routes/Forbidden").then((m) => ({ default: m.Forbidden })),
);
const AdminCourseList = lazy(() =>
  import("@/admin/routes/courses/CourseList").then((m) => ({ default: m.CourseList })),
);
const AdminCourseCreate = lazy(() =>
  import("@/admin/routes/courses/CourseCreate").then((m) => ({ default: m.CourseCreate })),
);
const AdminCourseEdit = lazy(() =>
  import("@/admin/routes/courses/CourseEditTabs").then((m) => ({
    default: m.CourseEditTabs,
  })),
);
const AdminLessonEditor = lazy(() =>
  import("@/admin/routes/courses/LessonEditor").then((m) => ({ default: m.LessonEditor })),
);
const AdminCourseProgress = lazy(() =>
  import("@/admin/routes/courses/CourseProgress").then((m) => ({ default: m.CourseProgress })),
);
const AdminCourseGrading = lazy(() =>
  import("@/admin/routes/courses/CourseGrading").then((m) => ({ default: m.CourseGrading })),
);
// Sprint 12 B5 — Live Classroom (WebRTC qua LiveKit)
const AdminLiveClassList = lazy(() =>
  import("@/admin/routes/live/LiveClassList").then((m) => ({ default: m.LiveClassList })),
);
const CourseLive = lazy(() =>
  import("@/routes/CourseLive").then((m) => ({ default: m.CourseLive })),
);
const LiveClassJoinPage = lazy(() =>
  import("@/routes/LiveClassJoinPage").then((m) => ({ default: m.LiveClassJoinPage })),
);
const AdminQuizBuilder = lazy(() =>
  import("@/admin/routes/courses/QuizBuilder/QuizBuilder").then((m) => ({
    default: m.QuizBuilder,
  })),
);
const AdminUserList = lazy(() =>
  import("@/admin/routes/users/UserList").then((m) => ({ default: m.UserList })),
);
const AdminUserEdit = lazy(() =>
  import("@/admin/routes/users/UserEdit").then((m) => ({ default: m.UserEdit })),
);
const AdminBulkAssignMajor = lazy(() =>
  import("@/admin/routes/users/BulkAssignMajor").then((m) => ({ default: m.BulkAssignMajor })),
);
const AdminAnalytics = lazy(() =>
  import("@/admin/routes/analytics/AnalyticsDashboard").then((m) => ({
    default: m.AnalyticsDashboard,
  })),
);
const AdminOrderList = lazy(() =>
  import("@/admin/routes/orders/OrderList").then((m) => ({ default: m.OrderList })),
);
const AdminAuditLogList = lazy(() =>
  import("@/admin/routes/audit/AuditLogList").then((m) => ({ default: m.AuditLogList })),
);
const AdminTermOfferingList = lazy(() =>
  import("@/admin/routes/term-offerings/TermOfferingList").then((m) => ({
    default: m.TermOfferingList,
  })),
);
const AdminTuitionRateList = lazy(() =>
  import("@/admin/routes/tuition/TuitionRateList").then((m) => ({
    default: m.TuitionRateList,
  })),
);
const AdminGradingQueue = lazy(() =>
  import("@/admin/routes/grading/GradingQueue").then((m) => ({ default: m.GradingQueue })),
);
const AdminCurriculumList = lazy(() =>
  import("@/admin/routes/curriculum/CurriculumList").then((m) => ({
    default: m.CurriculumList,
  })),
);
const AdminMajorDetail = lazy(() =>
  import("@/admin/routes/curriculum/MajorDetail").then((m) => ({
    default: m.MajorDetail,
  })),
);

// Fallback shown when admin chunk đang load — center spinner full-page
function AdminRouteFallback() {
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Spin size="large" />
    </div>
  );
}

function App() {
  return (
    <Routes>
      {/* Public auth routes — không AppLayout */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/verify-email/:token" element={<EmailVerify />} />
      <Route path="/forget-password" element={<ForgetPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      {/* Sprint 9 B1 — public invite accept (admin invited user set password) */}
      <Route path="/invite/:token" element={<InviteAccept />} />

      {/* Payment return — public (PayOS redirect về không có session) */}
      <Route path="/payment/success" element={<PaymentReturn />} />
      <Route path="/payment/cancel" element={<PaymentReturn />} />

      {/* Sprint 24 B4 — Career Connect public routes (anonymous OK, no ProtectedRoute) */}
      <Route
        path="/careers"
        element={
          <Suspense fallback={<AdminRouteFallback />}>
            <CareerLanding />
          </Suspense>
        }
      />
      <Route
        path="/careers/paths/:slug"
        element={
          <Suspense fallback={<AdminRouteFallback />}>
            <CareerPathDetail />
          </Suspense>
        }
      />
      <Route
        path="/careers/partners/:slug"
        element={
          <Suspense fallback={<AdminRouteFallback />}>
            <CareerPartnerDetail />
          </Suspense>
        }
      />
      <Route
        path="/careers/privacy"
        element={
          <Suspense fallback={<AdminRouteFallback />}>
            <CareersPrivacy />
          </Suspense>
        }
      />
      <Route
        path="/careers/register"
        element={
          <Suspense fallback={<AdminRouteFallback />}>
            <CareerRegister />
          </Suspense>
        }
      />

      {/* Protected — AppLayout wrap + auth required */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Home />} />
        <Route path="/courses" element={<CourseList />} />
        <Route path="/courses/:slug" element={<CourseDetail />} />
        {/* Việc B — Ôn tập tổng hợp SRS theo khoá */}
        <Route path="/courses/:slug/srs" element={<CourseSrsReview />} />
        <Route path="/lessons/:slug" element={<LessonDetail />} />
        <Route path="/my-courses" element={<MyCourses />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/majors/:slug" element={<MajorPublicDetail />} />
        <Route path="/study-plan" element={<StudyPlan />} />
        <Route path="/my-orders" element={<MyOrders />} />
        {/* Sprint 16 B2 — Adaptive Learning Path (Pillar P2) */}
        <Route path="/my-progress" element={<MyProgress />} />
        <Route path="/courses/:slug/quizzes" element={<QuizList />} />
        <Route path="/quizzes/:quizId/consent" element={<QuizConsent />} />
        <Route path="/quizzes/attempts/:attemptId/take" element={<QuizTake />} />
        <Route path="/quizzes/attempts/:attemptId/review" element={<QuizReview />} />
        <Route path="/huong-dan-su-dung" element={<UserGuide />} />
        {/* Sprint 12 B5 — Live Classroom. Dùng chung cho instructor lẫn student
            (quyền do JWT server cấp, xem docs/plans/2026-07-28-live-classroom-webrtc-plan.md). */}
        <Route
          path="/courses/:slug/live"
          element={
            <Suspense fallback={<Spin style={{ margin: "64px auto", display: "block" }} />}>
              <CourseLive />
            </Suspense>
          }
        />
        <Route
          path="/live/:id/room"
          element={
            <Suspense fallback={<Spin style={{ margin: "64px auto", display: "block" }} />}>
              <LiveClassJoinPage />
            </Suspense>
          }
        />
      </Route>

      {/* Admin namespace — guard by role (instructor/tenant_admin/superadmin) (Sprint 8A B1).
          Sprint 10 B5 — Suspense wrap để lazy admin chunk load với spinner fallback. */}
      <Route
        path="/admin"
        element={
          <AdminRouteGuard>
            <AdminLayout />
          </AdminRouteGuard>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminDashboard />
            </Suspense>
          }
        />
        <Route
          path="courses"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminCourseList />
            </Suspense>
          }
        />
        <Route
          path="courses/new"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminCourseCreate />
            </Suspense>
          }
        />
        <Route
          path="courses/:slug"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminCourseEdit />
            </Suspense>
          }
        />
        <Route
          path="courses/:slug/progress"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminCourseProgress />
            </Suspense>
          }
        />
        <Route
          path="courses/:slug/grading"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminCourseGrading />
            </Suspense>
          }
        />
        <Route
          path="courses/:slug/live"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminLiveClassList />
            </Suspense>
          }
        />
        <Route
          path="courses/:courseSlug/lessons/:slug"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminLessonEditor />
            </Suspense>
          }
        />
        <Route
          path="courses/:courseSlug/quizzes/:quizId"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminQuizBuilder />
            </Suspense>
          }
        />
        <Route
          path="users"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminUserList />
            </Suspense>
          }
        />
        <Route
          path="users/bulk-import"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminBulkAssignMajor />
            </Suspense>
          }
        />
        <Route
          path="users/:id"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminUserEdit />
            </Suspense>
          }
        />
        <Route
          path="analytics"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminAnalytics />
            </Suspense>
          }
        />
        <Route
          path="orders"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminOrderList />
            </Suspense>
          }
        />
        <Route
          path="audit"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminAuditLogList />
            </Suspense>
          }
        />
        {/* Sprint 24 B5 — Career Connect admin */}
        <Route
          path="careers/interests"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminCareerInterests />
            </Suspense>
          }
        />
        <Route
          path="proctor"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminProctorReview />
            </Suspense>
          }
        />
        <Route
          path="term-offerings"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminTermOfferingList />
            </Suspense>
          }
        />
        <Route
          path="tuition-rates"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminTuitionRateList />
            </Suspense>
          }
        />
        <Route
          path="grading"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminGradingQueue />
            </Suspense>
          }
        />
        <Route
          path="curriculum"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminCurriculumList />
            </Suspense>
          }
        />
        <Route
          path="curriculum/majors/:id"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminMajorDetail />
            </Suspense>
          }
        />
        {/* Sprint 25 B5 — AI Lesson Designer (chuyển vào /admin — trước đây
            /teach/designer nằm ngoài admin, chỉ tự check role trong page) */}
        <Route
          path="designer"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <DesignerHome />
            </Suspense>
          }
        />
        <Route
          path="designer/new"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <DesignerWizard />
            </Suspense>
          }
        />
        <Route
          path="designer/jobs/:id"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <DesignerWizard />
            </Suspense>
          }
        />
        <Route
          path="forbidden"
          element={
            <Suspense fallback={<AdminRouteFallback />}>
              <AdminForbidden />
            </Suspense>
          }
        />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;
